/**
 * BarcodeLabel — etiqueta de código de barras (térmica 80 mm ou A4)
 * Nome, SKU, referência e código de barras (EAN-13 / EAN-8 / CODE128).
 */

import { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import { resolveBarcodeFormat, getPrimaryReference } from '../../../../utils/barcodeLabel';

function scaleBarcodeOptions(labelWidthMm, labelHeightMm) {
  const w = Number(labelWidthMm) || 80;
  const h = Number(labelHeightMm) || 45;
  const compact = w < 70 || h < 42;

  return {
    barWidth: compact ? (w < 55 ? 1.1 : 1.3) : 1.8,
    barWidthCode128: compact ? 1.0 : 1.4,
    barHeight: compact ? Math.max(36, Math.round(h * 1.1)) : 58,
    fontSize: compact ? 10 : 13,
    margin: compact ? 1 : 2,
  };
}

function BarcodeLabel({
  product,
  className = '',
  widthMm,
  heightMm,
  style = {},
}) {
  const svgRef = useRef(null);
  const barcode = product?.barcode ? String(product.barcode).trim() : '';
  const resolved = resolveBarcodeFormat(barcode);
  const reference = getPrimaryReference(product);
  const sizeStyle = {
    ...(widthMm ? { width: `${widthMm}mm` } : null),
    ...(heightMm ? { height: `${heightMm}mm` } : null),
    ...style,
  };

  const compact = (Number(widthMm) || 80) < 70 || (Number(heightMm) || 45) < 42;

  useEffect(() => {
    if (!svgRef.current || !resolved) return;

    const opts = scaleBarcodeOptions(widthMm, heightMm);

    try {
      JsBarcode(svgRef.current, resolved.value, {
        format: resolved.format,
        width: resolved.format === 'CODE128' ? opts.barWidthCode128 : opts.barWidth,
        height: opts.barHeight,
        displayValue: true,
        fontSize: opts.fontSize,
        margin: opts.margin,
        textMargin: 2,
        background: '#ffffff',
        lineColor: '#000000',
      });
    } catch (err) {
      console.warn('[BarcodeLabel] Falha ao renderizar barcode:', err.message);
      if (svgRef.current) {
        svgRef.current.innerHTML = '';
      }
    }
  }, [resolved, widthMm, heightMm]);

  return (
    <div
      className={`barcode-label ${compact ? 'is-compact' : ''} ${className}`.trim()}
      style={sizeStyle}
    >
      <div className="barcode-label__name" title={product?.name || ''}>
        {product?.name || '—'}
      </div>
      <div className="barcode-label__meta">
        <span className="barcode-label__sku">SKU {product?.sku || '—'}</span>
        {reference ? (
          <span className="barcode-label__ref">REF {reference}</span>
        ) : null}
      </div>
      <div className="barcode-label__bars">
        {resolved ? (
          <svg ref={svgRef} />
        ) : (
          <span className="barcode-label__missing">Sem código de barras</span>
        )}
      </div>
    </div>
  );
}

export default BarcodeLabel;
